import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  X, Save, User, Package, MapPin, Search, Tag, AlertCircle, ShoppingBag, Truck, Eye, ChevronDown, ArrowRight
} from 'lucide-react';
import { Modal, Button, Badge } from '../../../components/ui';
import { FormInput, FormSelect, FormTextarea } from './SharedFormComponents';
import { SelectedItemsTable } from './SelectedItemsTable';
import { useSaleEntryForm } from '../hooks/useSaleEntryForm';
import { ItemDetailsModal } from './ItemDetailsContent';
import InvoiceSale from '../../../utils/InvoiceSale';
import api from '../../../lib/API';
import toast from 'react-hot-toast';
import { useCompanyInfo } from '../../verve-settings/hooks/useCompanyInfo';

const CustomFilterDropdown = ({ label, value, onChange, options, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOpt = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <label className="text-slate-700 font-bold text-[11px] tracking-wide capitalize">{label}</label>
      <div className="relative">
        <div 
          className={`flex items-center w-full border border-slate-200 rounded-md px-3 appearance-none text-[13px] font-medium text-slate-800 transition-all bg-white shadow-sm cursor-pointer ${isOpen ? 'border-slate-800' : ''} ${className || 'h-[35px]'}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="w-full truncate pr-4">{selectedOpt ? selectedOpt.label : ""}</div>
        </div>
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        
        {isOpen && (
          <ul className="absolute z-[60] w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-48 overflow-y-auto text-[11px] font-medium py-1">
            {options.map((opt, idx) => (
              <li 
                key={idx}
                className={`px-3 py-2 cursor-pointer whitespace-normal break-words leading-relaxed transition-colors hover:bg-slate-100 hover:text-slate-900 ${value === opt.value ? 'bg-slate-50 text-brand-dark font-bold' : 'text-slate-700'}`}
                onClick={() => { onChange({ target: { value: opt.value } }); setIsOpen(false); }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const SaleEntryModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  dynamicSettings = {}, 
  loading = false 
}) => {
  const { company } = useCompanyInfo();
  const { form, errors, sameAsBilling, setSameAsBilling, updateField, validateForm, resetForm } = useSaleEntryForm();

  const [previewPayload, setPreviewPayload] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredPreview, setHoveredPreview] = useState(null);
  
  // Formatters
  const fmt = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0);
  const [purchaseMasterData, setPurchaseMasterData] = useState([]);
  const [showItemDetails, setShowItemDetails] = useState(false);
  const scrollRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchPurchaseMaster = async () => {
        try {
          const res = await api.get('/v1/purchase-master');
          if (res.data) {
            setPurchaseMasterData(res.data.data || res.data || []);
          }
        } catch (e) {
          console.error('Failed to fetch purchase master data', e);
        }
      }
      fetchPurchaseMaster();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setSelectedItems([]);
      setSelectedDetailsItem(null);
      setSearchTerm("");
            setFilterVendor('all');
      setFilterCategory('all');
      setFilterSubCategory('all');
      setSameAsBilling(false);
    }
  }, [isOpen]);



  const resolveVendor = (i) => {
    if (!i) return 'Unknown';
    if (i.vendorName) return i.vendorName;
    if (typeof i.vendor === 'object' && i.vendor) return i.vendor.vendorName || i.vendor.name || i.vendor.title || 'Unknown';
    if (i.vendor) return i.vendor;
    if (typeof i.supplier === 'object' && i.supplier) return i.supplier.name || i.supplier.title || 'Unknown';
    if (i.supplier) return i.supplier;
    return 'blum';
  };


  const uniqueProducts = useMemo(() => {
    const uniqueMap = new Map();
    // Use purchase master array
    (purchaseMasterData || []).forEach(item => {
      if (item.status === 'Inactive') return;

      const name = (item.information || item.description || item.itemName || '').trim();
      if (name && !uniqueMap.has(name)) {
        uniqueMap.set(name, item);
      }
    });
    return Array.from(uniqueMap.values());
  }, [purchaseMasterData]);

    const vendorOptions = useMemo(() => {
    const v = new Set(uniqueProducts.map(i => resolveVendor(i)).filter(Boolean));
    return [{ label: 'All Vendors', value: 'all' }, ...Array.from(v).map(x => ({ label: x, value: x }))];
  }, [uniqueProducts]);

  const categoryOptions = useMemo(() => {
    let items = uniqueProducts;
    if (filterVendor !== 'all') {
      items = items.filter(i => (resolveVendor(i)) === filterVendor);
    }
    const c = new Set(items.map(i => i.category || i.categoryName).filter(Boolean));
    return [{ label: 'All Categories', value: 'all' }, ...Array.from(c).map(x => ({ label: x, value: x }))];
  }, [uniqueProducts, filterVendor]);

  const subCategoryOptions = useMemo(() => {
    let items = uniqueProducts;
    if (filterVendor !== 'all') {
      items = items.filter(i => (resolveVendor(i)) === filterVendor);
    }
    if (filterCategory !== 'all') {
      items = items.filter(i => (i.category || i.categoryName) === filterCategory);
    }
    const sc = new Set(items.map(i => i.subCategory).filter(Boolean));
    return [{ label: 'All Sub Categories', value: 'all' }, ...Array.from(sc).map(x => ({ label: x, value: x }))];
  }, [uniqueProducts, filterVendor, filterCategory]);

  const availableItems = useMemo(() => {
    let items = uniqueProducts;
    if (filterVendor !== 'all') {
      items = items.filter(i => (resolveVendor(i)) === filterVendor);
    }
    if (filterCategory !== 'all') {
      items = items.filter(i => (i.category || i.categoryName) === filterCategory);
    }
    if (filterSubCategory !== 'all') {
      items = items.filter(i => i.subCategory === filterSubCategory);
    }
    
    if (!searchTerm) return items;
    
    const term = searchTerm.toLowerCase();
    return items.filter(item => {
      return (
        (item.information || '').toLowerCase().includes(term) ||
        (item.description || '').toLowerCase().includes(term) ||
        (item.itemName || item.name || '').toLowerCase().includes(term) ||
        (item.articleId || item.itemCode || '').toLowerCase().includes(term) ||
        (item.category || item.categoryName || '').toLowerCase().includes(term) ||
        (item.subCategory || '').toLowerCase().includes(term)
      );
    });
  }, [uniqueProducts, filterVendor, filterCategory, filterSubCategory, searchTerm]);

  const handleToggleSelect = (item, checked) => {
    if (checked) {
      if (!selectedItems.find(i => i.item._id === item._id)) {
        setSelectedItems(prev => [
          ...prev, 
          { 
            item, 
            qty: 1, 
            price: item.vervePrice || item.blumPrice || 0 
          }
        ]);
      }
    } else {
      setSelectedItems(prev => prev.filter(i => i.item._id !== item._id));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...selectedItems];
    newItems[index][field] = value;
    setSelectedItems(newItems);
  };

  const removeItem = (index) => {
    const newItems = [...selectedItems];
    newItems.splice(index, 1);
    setSelectedItems(newItems);
  };

  const invoiceTotal = selectedItems.reduce((sum, i) => {
    const itemTotal = Number(i.qty || 0) * Number(String(i.price || 0).replace(/,/g, ''));
    const disAmt = itemTotal * (Number(i.disPercent || 0) / 100);
    return sum + (itemTotal - disAmt);
  }, 0);
  const discountAmount = Number(form.discountAmount || 0) || (invoiceTotal * (Number(form.discountPercent || 0) / 100));
  const taxableAmount = invoiceTotal - discountAmount;
  const calculatedTaxAmount = taxableAmount * (Number(form.taxPercent || 18) / 100);
  const netBill = taxableAmount + calculatedTaxAmount;
  const autoRoundedNetBill = Math.round(netBill);
  const finalNetPayment = form.roundOff !== undefined && form.roundOff !== "" ? Number(form.roundOff) : autoRoundedNetBill;

  const isAnyOverdraft = selectedItems.some(i => Number(i.qty || 0) > Number(i.item.availableQty ?? i.item.stock ?? i.item.quantity ?? 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors before submitting.");
      return;
    }
    if (!form.saleType) {
      toast.error("Please select a Sale Type.");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to sell.");
      return;
    }
    
    // validate overdraft and zero qty
    for (const i of selectedItems) {
      if (!i.qty || Number(i.qty) <= 0) {
        toast.error(`Please enter a valid quantity for ${i.item.articleId || 'item'}`);
        return;
      }
      const available = Number(i.item.availableQty ?? i.item.stock ?? i.item.quantity ?? 0);
      if (Number(i.qty) > available) {
        toast.error(`Cannot sell more than available stock for ${i.item.articleId || 'item'}!`);
        return;
      }
    }

    if (!form.customer) {
      toast.error("Customer Name is required.");
      return;
    }

    const payload = {
      date: new Date().toISOString().split('T')[0],
      customer: form.customer,
      customerPhone: form.customerPhone,
      reference: form.reference,
      customerEmail: form.customerEmail,
      billingAddressLine1: form.billingAddressLine1,
      billingAddressLine2: form.billingAddressLine2,
      billingCity: form.billingCity,
      billingState: form.billingState,
      billingPincode: form.billingPincode,
      saleType: form.saleType,
      remarks: form.remarks,
      
      // Delivery Fields
      deliveryContactPerson: form.deliveryContactPerson,
      deliveryPhone: form.deliveryPhone,
      deliveryAddressLine1: form.deliveryAddressLine1,
      deliveryAddressLine2: form.deliveryAddressLine2,
      deliveryCity: form.deliveryCity,
      deliveryState: form.deliveryState,
      deliveryPincode: form.deliveryPincode,
      courierName: form.courierName,
      trackingNumber: form.trackingNumber,
      deliveryDate: form.deliveryDate,
      deliveryStatus: form.deliveryStatus,
      deliveryNotes: form.deliveryNotes,
      
      // Store exact calculated totals
      totalDiscount: discountAmount,
      taxableAmount: taxableAmount,
      totalTax: calculatedTaxAmount,
      netBill: netBill,
      adjustment: form.roundOff !== undefined && form.roundOff !== "" ? Number(form.roundOff) - autoRoundedNetBill : 0,
      netPayable: finalNetPayment,
      paidAmount: Number(form.paidAmount || 0),
      balanceDue: finalNetPayment - Number(form.paidAmount || 0),

      items: selectedItems.map(i => {
        const qty = Number(i.qty);
        const rate = Number(String(i.price || 0).replace(/,/g, ''));
        const total = qty * rate;
        const disPercent = Number(i.disPercent || 0);
        const discountAmt = total * (disPercent / 100);
        const amount = total - discountAmt;
        return {
          item: i.item,
          qty: qty,
          unit: i.item.uom || "Nos",
          rate: rate,
          taxPercent: Number(form.taxPercent || 18),
          taxAmount: amount * (Number(form.taxPercent || 18) / 100),
          discountPercent: disPercent,
          amount: amount,
        };
      }),
      store: null
    };

    setPreviewPayload(payload);
  };

  if (!isOpen) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={previewPayload ? "INVOICE PREVIEW" : "Record New Sale"} 
      size="xl"
      closeButtonClassName={previewPayload ? "hidden" : ""}
    >
      <div className="flex h-full flex-col bg-[#fcfcfd] relative" ref={scrollRef}>
        <div className="flex-1 overflow-y-auto p-8 pt-6 pb-24">
          
          {previewPayload ? (
            <InvoiceSale 
              data={previewPayload}
              settings={{...dynamicSettings, ...company}}
              onClose={() => setPreviewPayload(null)} 
              onSave={async () => {
                const backendPayload = {
                  ...previewPayload,
                  items: previewPayload.items.map(i => ({
                    ...i,
                    item: i.item._id || i.item
                  }))
                };
                const success = await onSubmit(backendPayload);
                if (success) {
                  setPreviewPayload(null);
                }
                return success;
              }}
            />
          ) : (
          <div className="max-w-4xl mx-auto space-y-10">

            {/* SECTION 1: SALE DETAILS */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                <h3 className="text-[11px] font-bold text-slate-400 tracking-[0.15em] capitalize">01. Sale Details</h3>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormSelect
                  label="Sale Type"
                  value={form.saleType}
                  placeholder="Select Type"
                  onChange={(e) => updateField('saleType', e.target.value)}
                  error={errors.saleType}
                  options={[
                    { label: 'Direct', value: 'Direct' },
                    { label: 'Project', value: 'Project' }
                  ]}
                />
                <FormInput
                  label="Customer Name"
                  value={form.customer}
                  onChange={(e) => updateField('customer', e.target.value)}
                  placeholder="Enter customer name"
                  error={errors.customer}
                />
                <FormInput
                  label="Phone Number"
                  value={form.customerPhone}
                  onChange={(e) => updateField('customerPhone', e.target.value)}
                  placeholder="Enter phone number"
                  error={errors.customerPhone}
                />
                <FormInput
                  label="Email Address"
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) => updateField('customerEmail', e.target.value)}
                  placeholder="Enter email address"
                  error={errors.customerEmail}
                />
                <FormInput
                  label="Reference"
                  value={form.reference}
                  onChange={(e) => updateField('reference', e.target.value)}
                  placeholder="Enter reference (e.g. Architect/Designer)"
                />
              </div>
              </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SECTION 2: BILLING DETAILS */}
              <div className="space-y-4 p-6 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                  <h3 className="text-[11px] font-bold text-slate-400 tracking-[0.15em] capitalize">02. Billing Details</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Address Line 1" value={form.billingAddressLine1} onChange={(e) => updateField('billingAddressLine1', e.target.value)} placeholder="Enter address line 1" />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Address Line 2" value={form.billingAddressLine2} onChange={(e) => updateField('billingAddressLine2', e.target.value)} placeholder="Enter address line 2" />
                  </div>
                  <FormInput label="City" value={form.billingCity} onChange={(e) => updateField('billingCity', e.target.value)} placeholder="Enter city" />
                  <FormInput label="State" value={form.billingState} onChange={(e) => updateField('billingState', e.target.value)} placeholder="Enter state" />
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Pincode" value={form.billingPincode} onChange={(e) => updateField('billingPincode', e.target.value)} placeholder="Enter pincode" error={errors.billingPincode} />
                  </div>
                </div>
              </div>

              {/* SECTION 3: DELIVERY DETAILS */}
              <div className="space-y-4 p-6 bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[11px] font-bold text-slate-400 tracking-[0.15em] capitalize">03. Delivery Details</h3>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} className="w-3.5 h-3.5 text-brand-dark border-slate-300 rounded focus:ring-brand-dark cursor-pointer" />
                    Same as billing
                  </label>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Address Line 1" value={form.deliveryAddressLine1} onChange={(e) => updateField('deliveryAddressLine1', e.target.value)} placeholder="Enter address line 1" disabled={sameAsBilling} />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Address Line 2" value={form.deliveryAddressLine2} onChange={(e) => updateField('deliveryAddressLine2', e.target.value)} placeholder="Enter address line 2" disabled={sameAsBilling} />
                  </div>
                  <FormInput label="City" value={form.deliveryCity} onChange={(e) => updateField('deliveryCity', e.target.value)} placeholder="Enter city" disabled={sameAsBilling} />
                  <FormInput label="State" value={form.deliveryState} onChange={(e) => updateField('deliveryState', e.target.value)} placeholder="Enter state" disabled={sameAsBilling} />
                  <div className="col-span-1 sm:col-span-2">
                    <FormInput label="Pincode" value={form.deliveryPincode} onChange={(e) => updateField('deliveryPincode', e.target.value)} placeholder="Enter pincode" disabled={sameAsBilling} error={errors.deliveryPincode} />
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 4: ITEM CONFIGURATION */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                <h3 className="text-[11px] font-bold text-slate-400 tracking-[0.15em] capitalize">04. Item Configuration</h3>
              </div>
              
              <div className="relative mt-2" ref={searchContainerRef}>
                <div className="flex flex-col md:flex-row gap-4 mb-2 p-4 bg-slate-50/50 rounded-xl border border-slate-100 items-end">
                  <div className="w-full md:w-1/3">
                    <CustomFilterDropdown
                      label="Vendor"
                      value={filterVendor}
                      onChange={(e) => { setFilterVendor(e.target.value); setFilterCategory('all'); setFilterSubCategory('all'); }}
                      options={vendorOptions}
                    />
                  </div>
                  <div className="w-full md:flex-1">
                      <label className="text-slate-700 font-bold text-[11px] tracking-wide capitalize mb-1 block">
                        Search
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-slate-400" />
                        </div>
                    <input
                      type="text"
                      placeholder="Search by article ID, description, category..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      className="w-full pl-10 text-[13px] font-medium text-slate-800 bg-white border border-slate-200 focus:border-slate-800 transition-all h-[35px] shadow-sm rounded-md"
                    />
                    {showDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-2xl border border-slate-200 h-[400px] flex overflow-hidden">
                        
                        {/* LEFT PANE: Search Results */}
                        <div className="w-[55%] flex flex-col border-r border-slate-200 bg-white">
                          <div className="flex-1 overflow-auto">
                            {availableItems.length > 0 ? (
                              availableItems.map((item, idx) => {
                                const isSelected = selectedItems.some(i => i.item._id === item._id);
                                return (
                                  <div
                                    key={idx}
                                    onMouseEnter={() => setHoveredPreview(item)}
                                    className="px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex justify-between items-center transition-colors cursor-default"
                                  >
                                    <div className="flex items-center gap-4 flex-1">
                                      <input 
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => handleToggleSelect(item, e.target.checked)}
                                        className="w-4 h-4 text-brand-dark border-slate-300 rounded focus:ring-brand-dark cursor-pointer"
                                      />
                                      <div>
                                        <div className="text-[13px] font-bold text-slate-800 line-clamp-1">{item.information || item.description || item.itemName || item.name}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2">
                                          <span>ID: {item.articleId || item.itemCode || item.code || 'N/A'}</span>
                                          {item.category && <span>• {item.category}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                      <div className="text-right">
                                        <div className="text-[12px] font-bold text-brand-dark">{Number(item.availableQty ?? item.stock ?? item.quantity ?? 0)} available</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{resolveVendor(item)}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="p-8 text-center text-sm text-slate-500">No available items found matching "{searchTerm}"</div>
                            )}
                          </div>
                        </div>

                        {/* RIGHT PANE: Toolkit Preview */}
                        <div className="w-[45%] bg-slate-50/80 overflow-auto p-6">
                          {hoveredPreview ? (
                            <div className="animate-fade-in">
                              <h4 className="text-[12px] font-bold text-slate-400 capitalize tracking-widest mb-4 pb-2 border-b border-slate-200">
                                Item Details Preview
                              </h4>
                              
                              <div className="text-[13px] font-bold text-slate-900 mb-5 leading-snug break-words">
                                {hoveredPreview.information || hoveredPreview.description || hoveredPreview.itemName}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-y-4 gap-x-3">
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Article ID</span>
                                  <span className="text-[11px] font-semibold text-slate-900 break-all">{hoveredPreview.articleId || hoveredPreview.itemCode || '—'}</span>
                                </div>
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Category</span>
                                  <span className="text-[11px] font-semibold text-slate-900 break-words">{hoveredPreview.category || '—'}</span>
                                </div>
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Vendor</span>
                                  <span className="text-[11px] font-semibold text-slate-900 break-words">{resolveVendor(hoveredPreview)}</span>
                                </div>
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Available Stock</span>
                                  <span className="text-[12px] font-bold text-brand-dark">{Number(hoveredPreview.availableQty ?? hoveredPreview.stock ?? hoveredPreview.quantity ?? 0)}</span>
                                </div>
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Blum Price</span>
                                  <span className="text-[11px] font-semibold text-slate-900 break-words">{hoveredPreview.blumPrice ? fmt(hoveredPreview.blumPrice) : '—'}</span>
                                </div>
                                <div className="flex flex-col pr-1">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Verve Price</span>
                                  <span className="text-[11px] font-semibold text-slate-900 break-words">{hoveredPreview.vervePrice ? fmt(hoveredPreview.vervePrice) : '—'}</span>
                                </div>
                                <div className="flex flex-col col-span-2">
                                  <span className="text-[10px] font-semibold text-slate-500 mb-0.5">Remarks / Info</span>
                                  <span className="text-[11px] font-medium text-slate-700 break-words">{hoveredPreview.remark || hoveredPreview.remarks || '—'}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3">
                              <Eye size={32} className="opacity-20" />
                              <div className="text-sm font-medium">Hover over an item to preview details</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

                {/* Selected Items Table */}
              <SelectedItemsTable 
                selectedItems={selectedItems} 
                handleItemChange={handleItemChange} 
                removeItem={removeItem} 
              />
            </div>

            {/* FINAL NET PAYMENT */}
            <div className="mt-8 bg-[#111111] text-white p-6 rounded-xl shadow-lg flex items-center justify-between">
              <div>
                <div className="text-[12px] font-bold tracking-widest capitalize mb-1 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span> Final Net Payment
                </div>
                <div className="text-[10px] text-white/70 capitalize tracking-wider">Inclusive of all taxes and adjustments</div>
              </div>
              <div className="text-4xl font-black">
                ₹ {finalNetPayment.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
{/* Halimunnisa 10-06-2026 */}
        {/* FOOTER */}
        {!previewPayload && (
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 px-8 z-10 rounded-b-xl flex items-center justify-end shadow-[0_-4px_20px_rgba(0,0,0,0.02)]">
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
           <Button
  onClick={handleSubmit}
  loading={loading}
  disabled={isAnyOverdraft || selectedItems.length === 0}
  className="!bg-black !text-white hover:!bg-black px-8 flex items-center gap-2"
>
  Preview
  <ArrowRight size={16} />
</Button>
          </div>
        </div>
        )}
      

      {/* Item Details Modal */}
      {selectedDetailsItem && (
        <ItemDetailsModal item={selectedDetailsItem} onClose={() => setSelectedDetailsItem(null)} />
      )}
    </Modal>
  );
};
